import { useState, useEffect, useCallback } from 'react';
import {
  fetchProjectCustomers,
  addCustomerToProject,
  removeCustomerFromProject,
  setProjectPrimaryCustomer,
  setProjectBillingCustomer,
  updateProjectCustomer,
  fetchAvailableCustomers,
} from '../api/projectCustomersApi';
import { createCustomerWithPhones } from '@/features/customers/api/customersApi';
import type {
  ProjectCustomerView,
  ProjectCustomerRole,
  UpdateProjectCustomerInput,
} from '../types';
import type { CreateCustomerWithPhonesInput } from '@/features/customers/types';

interface UseProjectCustomersOptions {
  projectId: string | null;
  businessId: string | null;
  autoFetch?: boolean;
}

interface UseProjectCustomersReturn {
  customers: ProjectCustomerView[];
  availableCustomers: Array<{ id: string; first_name: string; last_name: string; email: string | null }>;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addCustomer: (
    customerId: string,
    role?: ProjectCustomerRole,
    isBilling?: boolean,
    notes?: string
  ) => Promise<boolean>;
  createAndAddCustomer: (
    input: Omit<CreateCustomerWithPhonesInput, 'business_id'>,
    role?: ProjectCustomerRole,
    isBilling?: boolean
  ) => Promise<boolean>;
  removeCustomer: (customerId: string) => Promise<boolean>;
  setPrimary: (projectCustomerId: string) => Promise<boolean>;
  setBilling: (projectCustomerId: string) => Promise<boolean>;
  updateCustomer: (
    projectCustomerId: string,
    input: UpdateProjectCustomerInput
  ) => Promise<boolean>;
  refreshAvailableCustomers: () => Promise<void>;
}

/**
 * Hook to manage customers for a project
 */
export function useProjectCustomers({
  projectId,
  businessId,
  autoFetch = true,
}: UseProjectCustomersOptions): UseProjectCustomersReturn {
  const [customers, setCustomers] = useState<ProjectCustomerView[]>([]);
  const [availableCustomers, setAvailableCustomers] = useState<
    Array<{ id: string; first_name: string; last_name: string; email: string | null }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCustomers = useCallback(async () => {
    if (!projectId) {
      setCustomers([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchProjectCustomers(projectId);
      setCustomers(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load project customers';
      setError(message);
      console.error('[useProjectCustomers] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  const loadAvailableCustomers = useCallback(async () => {
    if (!projectId || !businessId) {
      setAvailableCustomers([]);
      return;
    }

    try {
      const data = await fetchAvailableCustomers(businessId, projectId);
      setAvailableCustomers(data);
    } catch (err) {
      console.error('[useProjectCustomers] Error loading available customers:', err);
    }
  }, [projectId, businessId]);

  useEffect(() => {
    if (autoFetch && projectId) {
      loadCustomers();
      if (businessId) {
        loadAvailableCustomers();
      }
    }
  }, [autoFetch, projectId, businessId, loadCustomers, loadAvailableCustomers]);

  const addCustomer = useCallback(
    async (
      customerId: string,
      role: ProjectCustomerRole = 'contact',
      isBilling: boolean = false,
      notes?: string
    ): Promise<boolean> => {
      if (!projectId) return false;

      setIsUpdating(true);
      setError(null);

      try {
        await addCustomerToProject(projectId, customerId, role, isBilling, notes);
        await loadCustomers();
        await loadAvailableCustomers();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to add customer to project';
        setError(message);
        console.error('[useProjectCustomers] Error adding customer:', err);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [projectId, loadCustomers, loadAvailableCustomers]
  );

  const createAndAddCustomer = useCallback(
    async (
      input: Omit<CreateCustomerWithPhonesInput, 'business_id'>,
      role: ProjectCustomerRole = 'contact',
      isBilling: boolean = false
    ): Promise<boolean> => {
      if (!projectId || !businessId) return false;

      setIsUpdating(true);
      setError(null);

      try {
        // First create the customer with phones
        const newCustomer = await createCustomerWithPhones({
          ...input,
          business_id: businessId,
        });

        // Then add them to the project
        await addCustomerToProject(projectId, newCustomer.id, role, isBilling);
        await loadCustomers();
        await loadAvailableCustomers();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to create and add customer';
        setError(message);
        console.error('[useProjectCustomers] Error creating and adding customer:', err);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [projectId, businessId, loadCustomers, loadAvailableCustomers]
  );

  const removeCustomer = useCallback(
    async (customerId: string): Promise<boolean> => {
      if (!projectId) return false;

      setIsUpdating(true);
      setError(null);

      try {
        await removeCustomerFromProject(projectId, customerId);
        await loadCustomers();
        await loadAvailableCustomers();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to remove customer from project';
        setError(message);
        console.error('[useProjectCustomers] Error removing customer:', err);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [projectId, loadCustomers, loadAvailableCustomers]
  );

  const setPrimary = useCallback(
    async (projectCustomerId: string): Promise<boolean> => {
      if (!projectId) return false;

      setIsUpdating(true);
      setError(null);

      try {
        await setProjectPrimaryCustomer(projectId, projectCustomerId);
        await loadCustomers();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to set primary customer';
        setError(message);
        console.error('[useProjectCustomers] Error setting primary:', err);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [projectId, loadCustomers]
  );

  const setBilling = useCallback(
    async (projectCustomerId: string): Promise<boolean> => {
      if (!projectId) return false;

      setIsUpdating(true);
      setError(null);

      try {
        await setProjectBillingCustomer(projectId, projectCustomerId);
        await loadCustomers();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to set billing customer';
        setError(message);
        console.error('[useProjectCustomers] Error setting billing:', err);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [projectId, loadCustomers]
  );

  const updateCustomerFn = useCallback(
    async (
      projectCustomerId: string,
      input: UpdateProjectCustomerInput
    ): Promise<boolean> => {
      setIsUpdating(true);
      setError(null);

      try {
        await updateProjectCustomer(projectCustomerId, input);
        await loadCustomers();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update customer';
        setError(message);
        console.error('[useProjectCustomers] Error updating customer:', err);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [loadCustomers]
  );

  return {
    customers,
    availableCustomers,
    isLoading,
    isUpdating,
    error,
    refetch: loadCustomers,
    addCustomer,
    createAndAddCustomer,
    removeCustomer,
    setPrimary,
    setBilling,
    updateCustomer: updateCustomerFn,
    refreshAvailableCustomers: loadAvailableCustomers,
  };
}
